import React from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {Button, EMPHASIS, FlexColumn, FlexRow, Heading, Separator, Text} from '@forecast-it/design-system';
import {Card} from './Card';
import {formatDuration, formatHoursValue} from '../baselineTemplateFormat';
import {deliverableCount, expenseLineCount, TemplateDraft, templateDurationDays, totalHours} from './baselineTemplateDraft';

const Sticky = Styled.aside`
	position: sticky;
	top: 16px;
	width: 320px;
	flex-shrink: 0;
`;

type Props = {draft: TemplateDraft; submitting: boolean; onCreate: () => void; onCancel: () => void};

export const TemplateSummaryPanel = ({draft, submitting, onCreate, onCancel}: Props): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const days = templateDurationDays(draft);
	const deliverables = deliverableCount(draft);
	const line = (labelId: string, value: string | number, cy: string) => (
		<FlexRow justifyContent="space-between" data-cy={cy}>
			<Text color="auxiliary">{formatMessage({id: labelId})}</Text>
			<Text>{value}</Text>
		</FlexRow>
	);
	return (
		<Sticky>
			<Card data-cy="baseline-template-summary">
				<Heading size="4" as="h2">
					{formatMessage({id: 'baseline_templates.summary.title'})}
				</Heading>
				<FlexColumn gap="s">
					{line('baseline_templates.summary.phases', draft.phases.length, 'baseline-template-summary-phases')}
					{deliverables !== null
						? line(
								'baseline_templates.summary.deliverables',
								deliverables,
								'baseline-template-summary-deliverables'
						  )
						: null}
					{line(
						'baseline_templates.summary.work_estimate',
						formatHoursValue(intl, totalHours(draft)),
						'baseline-template-summary-hours'
					)}
					{line(
						'baseline_templates.summary.duration',
						formatDuration(intl, days),
						'baseline-template-summary-duration'
					)}
					{line(
						'baseline_templates.summary.expense_lines',
						expenseLineCount(draft),
						'baseline-template-summary-expenses'
					)}
				</FlexColumn>
				<Separator />
				<Text color="auxiliary">{formatMessage({id: 'baseline_templates.summary.note'})}</Text>
				<Separator />
				<FlexRow gap="s">
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={onCreate}
						disabled={submitting}
						data-cy="baseline-template-create"
					>
						{formatMessage({id: 'baseline_templates.create_template'})}
					</Button>
					<Button
						emphasis={EMPHASIS.MONO}
						onPress={onCancel}
						disabled={submitting}
						data-cy="baseline-template-cancel"
					>
						{formatMessage({id: 'common.cancel'})}
					</Button>
				</FlexRow>
			</Card>
		</Sticky>
	);
};
